/**
 * Suppress CSS transitions while the viewport is actively resizing.
 *
 * Transitions exist to animate STATE changes — a sheet opening, a button hovering. A viewport
 * resize is not a state change, but crossing the lg breakpoint re-resolves every responsive class
 * at once: the studio rail flips fixed→static, its max-height goes 68px→none, the tool rail turns
 * from a column into a strip. With transitions live, all of that animates mid-reflow and the studio
 * visibly tears when moving between mobile, tablet and desktop widths.
 *
 * While `html.resizing` is set, a global rule (index.css) forces `transition: none` — keyframe
 * animations (spinners, skeletons) are deliberately untouched. The class drops ~150ms after the
 * last resize event, so the moment the drag stops, everything animates normally again.
 */
let timer: number | undefined;
let crossFallback: number | undefined;

window.addEventListener(
  'resize',
  () => {
    document.documentElement.classList.add('resizing');
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      document.documentElement.classList.remove('resizing');
      /*
       * The cross-fade ends HERE — when the resize settles — never on its own clock. A fixed 240ms
       * timer used to drop `bp-cross` mid-drag, so anyone still resizing past the breakpoint saw
       * the shell pop back in its shifted position and THEN morph — "the entire preview shifts
       * before morphing". Tied to settle, the shell stays hidden for the whole drag and makes one
       * clean fade-in at the final size, which is why tablet↔mobile (no structural swap, no fade)
       * and desktop crossings now read the same.
       */
      document.documentElement.classList.remove('bp-cross');
      window.clearTimeout(crossFallback);
    }, 150);
  },
  { passive: true },
);

/*
 * Crossing the lg breakpoint is different in kind from an ordinary resize: the studio swaps between
 * two STRUCTURES (sidebar column ↔ bottom sheet, vertical rail ↔ horizontal strip). Suppressing
 * transitions stops the tearing, but the hard cut still reads as a glitch, so the crossing hides
 * the shell and the settle handler above fades it back in.
 */
const desktop = window.matchMedia('(min-width: 1024px)');
desktop.addEventListener('change', () => {
  document.documentElement.classList.add('bp-cross');
  /* safety net for a crossing with no follow-up resize events (device-mode toggles) */
  window.clearTimeout(crossFallback);
  crossFallback = window.setTimeout(() => {
    document.documentElement.classList.remove('bp-cross');
  }, 700);
});
